import { useEffect } from 'react';
import { saveToLocalStorage } from './playerUtils';
import { STORAGE_KEYS } from './types';

interface KeyboardShortcutsOptions {
  audioRef: React.RefObject<HTMLAudioElement>;
  duration: number;
  volume: number;
  setCurrentTime: (time: number) => void;
  setVolume: (volume: number) => void;
  setIsMuted: (muted: boolean) => void;
  onTogglePlay: () => void;
}

/**
 * Global keyboard controls for the player:
 * - ArrowLeft/Right: seek ±5s
 * - ArrowUp/Down: volume ±10%
 * - Space: toggle play/pause
 * Ignored while typing in INPUT/TEXTAREA.
 */
export function useKeyboardShortcuts({
  audioRef,
  duration,
  volume,
  setCurrentTime,
  setVolume,
  setIsMuted,
  onTogglePlay,
}: KeyboardShortcutsOptions) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if input elements are focused
      if (
        document.activeElement &&
        (document.activeElement.tagName === 'INPUT' ||
          document.activeElement.tagName === 'TEXTAREA')
      ) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          if (audioRef.current) {
            const newTime = Math.max(0, audioRef.current.currentTime - 5);
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
            saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, newTime);
          }
          break;
        case 'ArrowRight':
          if (audioRef.current) {
            const newTime = Math.min(
              audioRef.current.duration || duration,
              audioRef.current.currentTime + 5
            );
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
            saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, newTime);
          }
          break;
        case 'ArrowUp': {
          const newVolumeUp = Math.min(1, volume + 0.1);
          setVolume(newVolumeUp);
          if (audioRef.current) {
            audioRef.current.volume = newVolumeUp;
          }
          setIsMuted(newVolumeUp === 0);
          saveToLocalStorage(STORAGE_KEYS.VOLUME, newVolumeUp);
          break;
        }
        case 'ArrowDown': {
          const newVolumeDown = Math.max(0, volume - 0.1);
          setVolume(newVolumeDown);
          if (audioRef.current) {
            audioRef.current.volume = newVolumeDown;
          }
          setIsMuted(newVolumeDown === 0);
          saveToLocalStorage(STORAGE_KEYS.VOLUME, newVolumeDown);
          break;
        }
        case ' ': // Space key
          e.preventDefault();
          onTogglePlay();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [audioRef, duration, volume, setCurrentTime, setVolume, setIsMuted, onTogglePlay]);
}
